import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Switch, Text, View } from 'react-native';
import { setDoc, useDoc } from '../../store/store';
import { Chip, Field, Section, Sheet } from '../../components/ui';
import { BotaoAcao } from '../../components/ajustes/Linhas';
import { C, alpha } from '../../components/theme';
import { useFeriadosPref } from '../../components/useFeriados';
import { FeriadosSheet } from '../../components/FeriadosSheet';
import { CIDADES_BR, ESTADOS_BR, bandeira, type FeriadosPref } from '../../lib/feriados';
import { PAISES_MUNDO, nomePais, nomeRegiao, recarregar, regioesDoPais, situacao, useVersaoFeriados } from '../../lib/feriadosMundo';
import { norm, today } from '../../lib/date';
import { t } from '../../lib/i18n';
import { Card, Expansivel } from './comum';
import { ROLAGEM_TECLADO } from '../../components/teclado';

/** Linha marcável (caixinha) usada nas listas de estados e regiões. */
function Marca({ on, label, cor, onPress }: { on: boolean; label: string; cor: string; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, paddingHorizontal: 12,
      borderRadius: 12, backgroundColor: pressed ? C.card2 : on ? alpha(cor, 0.12) : 'transparent' })}>
      <View style={{ width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: on ? cor : C.mute, backgroundColor: on ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
        {on && <Text style={{ color: '#fff', fontWeight: '900', fontSize: 13 }}>✓</Text>}
      </View>
      <Text style={{ color: C.text, flex: 1 }}>{label}</Text>
    </Pressable>
  );
}

/** Regiões de um país (estados, províncias...), tiradas dos próprios feriados do ano. */
function RegioesPais({ pais, sel, cor, onToggle }: { pais: string; sel: string[]; cor: string; onToggle: (r: string) => void }) {
  useVersaoFeriados();
  const ano = Number(today().slice(0, 4));
  const sit = situacao(pais, ano);
  const regs = regioesDoPais(pais, ano);
  if (sit === 'carregando') return <Text style={{ color: C.sub, fontSize: 12 }}>{t('Carregando feriados...')}</Text>;
  if (sit === 'erro') return (
    <Pressable onPress={() => recarregar(pais, ano)}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Não foi possível baixar os feriados deste país. Confira a internet.')} <Text style={{ color: cor, fontWeight: '700' }}>{t('Tentar de novo')}</Text></Text>
    </Pressable>
  );
  if (sit === 'sem-dados') return <Text style={{ color: C.sub, fontSize: 12 }}>{t('Ainda não há feriados deste país para este ano.')}</Text>;
  if (!regs.length) return <Text style={{ color: C.sub, fontSize: 12 }}>{t('Este país só tem feriados nacionais.')}</Text>;
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>{t('Regiões com feriado próprio (marque as suas):')}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {regs.map(r => <Chip key={r} small cor={cor} on={sel.includes(r)} label={nomeRegiao(r)} onPress={() => onToggle(r)} />)}
      </View>
    </View>
  );
}

/** Ajustes > Feriados (0.67; antes "Visualizar feriados"): o país e a lista usados por todas as abas. Brasil com estados e cidades, e outros países com as regiões. */
export default function Feriados({ cor }: { cor: string }) {
  const pref = useFeriadosPref();
  const tasks = useDoc('planner').tasks;
  const [busca, setBusca] = useState('');
  const [addPais, setAddPais] = useState(false);
  const [ver, setVer] = useState(false);

  const salvar = (fn: (p: FeriadosPref) => FeriadosPref) => setDoc('ajustes', d => ({ ...d, feriados: fn(pref) }));
  const toggleRegiao = (pais: string, r: string) => salvar(p => {
    const atual = p.regioes[pais] ?? [];
    return { ...p, regioes: { ...p.regioes, [pais]: atual.includes(r) ? atual.filter(x => x !== r) : [...atual, r] } };
  });
  const regBR = pref.regioes.BR ?? [];
  const nEst = regBR.filter(r => /^[A-Z]{2}$/.test(r)).length, nCid = regBR.filter(r => r.includes('-')).length;
  const outros = pref.paises.filter(p => p !== 'BR');
  const lista = useMemo(() => PAISES_MUNDO.filter(([c]) => c !== 'BR' && !pref.paises.includes(c))
    .map(([c]) => [c, nomePais(c)] as const)
    .filter(([c, n]) => !busca.trim() || norm(n).includes(norm(busca)) || c.toLowerCase() === busca.trim().toLowerCase())
    .sort((a, b) => a[1].localeCompare(b[1])), [pref.paises, busca]);

  return (<>
    <Text style={{ color: C.sub }}>{t('Escolha os feriados que aparecem no Planner e no botão 🎉 Feriados.')}</Text>
    <BotaoAcao cor={cor} label={'🎉 ' + t('Ver os feriados do ano')} onPress={() => setVer(true)} />

    <Section t={`${bandeira('BR')} ${t('Brasil')}`} />
    <Card>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('Feriados nacionais')}</Text>
          <Text style={{ color: C.sub, fontSize: 12 }}>{pref.semNacionais ? t('Escondidos no Planner.')
            : t('Aparecem no Planner, junto com os pontos facultativos (Carnaval, Quarta-feira de Cinzas e Corpus Christi).')}</Text>
        </View>
        <Switch value={!pref.semNacionais} trackColor={{ true: cor }} onValueChange={v => salvar(p => ({ ...p, semNacionais: !v }))} />
      </View>
      <Expansivel titulo={t('Estados')} resumo={nEst ? t('{n} marcado(s)', { n: nEst }) : t('Toque para escolher')}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Marque um ou mais estados.')}</Text>
        <ScrollView style={{ maxHeight: 360 }} nestedScrollEnabled>
          {ESTADOS_BR.map(e => <Marca key={e.uf} cor={cor} on={regBR.includes(e.uf)} label={`${e.nome} (${e.uf})`} onPress={() => toggleRegiao('BR', e.uf)} />)}
        </ScrollView>
      </Expansivel>
      <Expansivel titulo={t('Cidades')} resumo={nCid ? t('{n} marcado(s)', { n: nCid }) : t('Toque para escolher')}>
        <Text style={{ color: C.sub, fontSize: 12 }}>{t('Todas as capitais. Marque uma ou mais cidades.')}</Text>
        <ScrollView style={{ maxHeight: 360 }} nestedScrollEnabled>
          {CIDADES_BR.map(c => <Marca key={c.id} cor={cor} on={regBR.includes(c.id)} label={c.nome} onPress={() => toggleRegiao('BR', c.id)} />)}
        </ScrollView>
      </Expansivel>
    </Card>

    <Section t={t('Outros países')} />
    {outros.map(p => (
      <Card key={p}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ fontSize: 22 }}>{bandeira(p)}</Text>
          <Text style={{ color: C.text, fontWeight: '700', flex: 1 }}>{nomePais(p)}</Text>
          <Pressable hitSlop={10} onPress={() => salvar(x => ({ ...x, paises: x.paises.filter(y => y !== p) }))}>
            <Text style={{ color: C.danger }}>{t('Remover')}</Text>
          </Pressable>
        </View>
        <RegioesPais pais={p} sel={pref.regioes[p] ?? []} cor={cor} onToggle={r => toggleRegiao(p, r)} />
      </Card>
    ))}
    <BotaoAcao cor={cor} label={'+ ' + t('Adicionar país')} onPress={() => { setBusca(''); setAddPais(true); }} />
    <Text style={{ color: C.mute, fontSize: 11 }}>
      {t('Fontes: Brasil, calculado no app a partir da portaria federal de feriados, das leis de cada estado e dos calendários oficiais das capitais. Outros países: base pública Nager.Date (date.nager.at), baixada pela internet e guardada no dispositivo.')}
    </Text>

    <Sheet visible={addPais} onClose={() => setAddPais(false)} title={t('Adicionar país')}>
      <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
        <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar país')} autoCorrect={false} />
      </View>
      <ScrollView {...ROLAGEM_TECLADO} contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 60, gap: 2 }} keyboardShouldPersistTaps="handled">
        {lista.map(([c, n]) => (
          <Pressable key={c} onPress={() => { salvar(p => ({ ...p, paises: [...p.paises, c] })); setAddPais(false); }}
            style={({ pressed }) => ({ flexDirection: 'row', gap: 12, alignItems: 'center', padding: 12, borderRadius: 12, backgroundColor: pressed ? C.card2 : 'transparent' })}>
            <Text style={{ fontSize: 22 }}>{bandeira(c)}</Text>
            <Text style={{ color: C.text, flex: 1 }}>{n}</Text>
          </Pressable>
        ))}
        {!lista.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 20 }}>{t('Nenhum país encontrado.')}</Text>}
      </ScrollView>
    </Sheet>
    <FeriadosSheet visible={ver} date={today()} tasks={tasks} cor={cor} onClose={() => setVer(false)} />
  </>);
}
